import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material'
import { IconX } from '@tabler/icons-react'
import { useMemo } from 'react'
import { omit } from 'remeda'
import { v4 } from 'uuid'
import { useImport } from '~/features/import-export/useImport'
import { useGetAllModels } from '~/features/models/models-list/hooks/useGetAllModels'
import { useImportModels } from '~/features/models/models-list/hooks/useImportModels'
import { modelSample } from '~/features/models/models-list/validators/modelValidators'

import { modelArraySchema } from '~/features/models/models-list/validators/modelValidators'
import { ModelV2 } from '~/supabase/types'

export const ImportModels = () => {
  const { ImportButton, excelData, reset } = useImport({
    schema: modelArraySchema,
    sample: modelSample,
  })
  const { data: models, isLoading: isLoadingModels } = useGetAllModels()

  const handleClose = () => {
    reset()
  }
  const duplicates = useMemo<Array<{ message: string; key: string }>>(() => {
    if (!models || !excelData) return []
    const modelsMap = new Map(models.map((m) => [m.stock_code, m]))

    return excelData.flatMap((d) =>
      modelsMap.get(d.stock_code)
        ? { message: `Model already exists - ${d.stock_code}`, key: d.stock_code }
        : []
    )
  }, [excelData, models])

  const modelsToImport = useMemo(() => {
    if (!excelData || !models) return []

    return excelData.map((d) => {
      const rest = omit(d, ['warning'])
      return { ...rest, image_urls: null, shopify_product_id: null } as const satisfies Omit<
        ModelV2,
        'id' | 'created_at' | 'updated_at'
      >
    })
  }, [excelData, models])

  const { mutate: importModels, isPending: isImporting } = useImportModels()

  const isLoading = isLoadingModels || isImporting

  return (
    <Box sx={{ width: 1, height: 1 }}>
      <Dialog
        open={modelsToImport.length > 0}
        PaperProps={{ sx: { minWidth: 400 } }}
        onClose={handleClose}
      >
        <Stack p={2} gap={2} overflow="hidden">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Import list</Typography>
            <IconButton onClick={handleClose}>
              <SvgIcon>
                <IconX />
              </SvgIcon>
            </IconButton>
          </Stack>

          {duplicates.length > 0 ? (
            <Box component={Card} px={2} py={1} elevation={0} variant="outlined" flexShrink={0}>
              {duplicates.map((d) => (
                <Typography key={d.key} color="warning">
                  {d.message}
                </Typography>
              ))}
            </Box>
          ) : null}
          <Box overflow="auto">
            {modelsToImport.map((row, i) => (
              <Stack
                key={v4()}
                direction="row"
                justifyContent="space-between"
                alignContent="center"
                px={2}
                py={1}
                my={1}
                component={Card}
                elevation={0}
                variant="outlined"
                sx={{
                  '&:before': {
                    content: `"${i + 1}"`,
                    color: (t) => t.palette.text.disabled,
                    fontSize: (t) => t.typography.caption.fontSize,
                  },
                }}
                gap={1}
              >
                <Typography flex={1}>{row.stock_code}</Typography>
                <Typography flex={1}>{row.supplier_code}</Typography>
              </Stack>
            ))}
          </Box>

          <Stack direction="row" justifyContent="space-between">
            <Typography>Total quantity {modelsToImport.length}</Typography>
            <Button
              variant="contained"
              disabled={isLoading || duplicates.length > 0}
              onClick={() => {
                importModels(modelsToImport, { onSuccess: handleClose })
              }}
              endIcon={isLoading ? <CircularProgress size={14} /> : null}
            >
              Import
            </Button>
          </Stack>
        </Stack>
      </Dialog>
      <ImportButton label="Import models" />
    </Box>
  )
}
